import React, { useMemo } from 'react';
import './scoreSparkline.css';

// Compact trend line for the score hero panel — a lightweight sibling of
// ScoreHistoryChart with no axis and no hover tooltip, showing only the most
// recent few assessments. Unlike ScoreHistoryChart it never fabricates a
// "baseline" reference line: the dashboard response carries no baseline
// value, so there is nothing honest to draw there (see
// docs/g-score-api-contract.md). Renders a graceful message instead of a
// broken or misleading chart whenever there isn't enough real data yet.
const MAX_POINTS = 4;

export default function ScoreSparkline({ entries, loading, error }) {
  const points = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    // entries arrive oldest -> newest (see useGScoreHistory / history contract);
    // keep only the most recent few for a compact sparkline.
    return entries
      .filter((e) => e && e.takenAt != null && e.composite != null)
      .slice(-MAX_POINTS);
  }, [entries]);

  if (loading) {
    return <div className="score-sparkline-placeholder" aria-hidden="true" />;
  }

  if (error) {
    return <p className="score-sparkline-empty">Couldn&apos;t load your recent history.</p>;
  }

  if (points.length < 2) {
    return (
      <p className="score-sparkline-empty">
        {points.length === 1
          ? 'One assessment recorded so far — one more starts your trend line.'
          : 'Your trend line appears after two assessments.'}
      </p>
    );
  }

  const width = 290;
  const height = 86;
  const padding = { top: 10, right: 4, bottom: 20, left: 4 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const scores = points.map((p) => p.composite);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = Math.max(1, max - min);

  const xFor = (i) => padding.left + (i / (points.length - 1)) * plotW;
  const yFor = (score) => padding.top + plotH - ((score - min) / range) * plotH;

  const linePoints = points.map((p, i) => `${xFor(i)},${yFor(p.composite)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', width: '100%', maxWidth: width, height: 'auto' }}
      role="img"
      aria-label="Composite G-Score over your most recent assessments"
    >
      <line
        x1={padding.left}
        x2={width - padding.right}
        y1={padding.top + plotH}
        y2={padding.top + plotH}
        stroke="var(--border-subtle)"
        strokeWidth={1}
      />
      <polyline
        points={linePoints}
        fill="none"
        stroke="var(--accent-primary)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p, i) => {
        const isLatest = i === points.length - 1;
        return (
          <circle
            key={p.runId || i}
            cx={xFor(i)}
            cy={yFor(p.composite)}
            r={isLatest ? 4.2 : 3.4}
            fill={isLatest ? 'var(--accent-primary)' : 'var(--bg-elevated)'}
            stroke="var(--accent-primary)"
            strokeWidth={2}
          />
        );
      })}
      {points.map((p, i) => (
        <text
          key={`${p.runId || i}-label`}
          x={xFor(i)}
          y={height - 4}
          textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
          fontFamily="var(--font-mono)"
          fontSize="10"
          fill="var(--text-tertiary)"
        >
          {formatDateShort(p.takenAt)}
        </text>
      ))}
    </svg>
  );
}

function formatDateShort(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
